/**
 * AikoMark — AISquare's brand mascot, single-file source of truth.
 *
 * EVERYTHING about the mark lives in this file. To update Aiko (different
 * paths, different animation, different container shape, different sizes)
 * touch ONLY this file — every consumer of `<AikoMark>` (tooltips, sheets,
 * loaders, marketing surfaces) inherits the change.
 *
 * Anatomy:
 *   - Container: rounded `--primary`-tinted square (the "hex"), sized via the
 *     `size` prop. Container radius scales with size (~25% of width).
 *   - SVG: hex-pentagon outline + breathing top + bouncing eyeball + blinking
 *     eyes. Paths and SMIL `<animate>` elements live inline below.
 *
 * Behavior:
 *   - Animated by default. Pass `animated={false}` to render the resting frame.
 *   - Honors `prefers-reduced-motion` automatically (resting frame).
 *   - Decorative by default (`aria-hidden="true"`). Pass `aria-label` to make
 *     it announce — the wrapper switches to `role="img"`.
 *
 * Tones:
 *   - `"primary"` (default) — `--primary` background, `--primary-foreground` mark.
 *     Used for inline Aiko avatars, chat, loaders.
 *   - `"steel"` — navy gradient container (the BrandTile treatment). Reads
 *     `--steel-bg`, `--steel-border`, `--steel-fg` CSS vars. This is the substrate
 *     that BrandTile composes — BrandTile is now a thin forwardRef wrapper over
 *     `<AikoMark tone="steel">`.
 *
 * Use as:
 *   - Inline avatar inside `<AikoTip>` / `<AikoSheet>` / `<AikoIntervene>`.
 *   - Standalone "thinking" indicator (the breathing reads as activity).
 *   - Marketing / hero surfaces (use `size="xl"` or wrap in your own container).
 *   - Office rail header brand anchor: `<BrandTile>` (thin composer over tone="steel").
 */
import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "../lib/cn";
import { useReducedMotion } from "../hooks/useReducedMotion";

/* ============================================================================
 * Visual constants — the "design knobs" for the entire mascot.
 * Updating these is the single-file change the team agreed on.
 * ========================================================================= */

/** Breathing loop duration. Matches the design's 4s breath. */
const BREATH_DURATION = "4s";
/** Same value in ms — used to compute the wall-clock-synced SMIL begin offset
 *  so multiple AikoMarks on the same page breathe in unison instead of each
 *  starting its own cycle at mount time. */
const BREATH_DURATION_MS = 4000;

/** Eye-blink visibility window inside the breath cycle. Matches the design. */
const BLINK_KEYTIMES = "0; 0.42; 0.5; 0.58; 1";
const BLINK_VALUES = "0; 0; 1; 0; 0";

/** Mark stroke + fill — reads from `--primary-foreground` so it inverts cleanly
 *  on whatever ink the consumer chose for the container. */
const MARK_STROKE = "currentColor";

/** SVG viewBox — paths reference this 480×480 grid. */
const VIEWBOX = "0 0 480 480";

/* ============================================================================
 * Container variants
 * ========================================================================= */

const markVariants = cva(
  cn(
    "inline-flex items-center justify-center flex-shrink-0",
    // 25%-of-width rounding gives the hex feel without being a perfect circle
    "rounded-[25%]",
  ),
  {
    variants: {
      size: {
        xs: "h-6 w-6",      // 24 — whisper pip
        sm: "h-7 w-7",      // 28 — default Aiko avatar
        md: "h-8 w-8",      // 32 — header / chat
        lg: "h-10 w-10",    // 40 — sheet headers
        xl: "h-14 w-14",    // 56 — hero / loader
        "2xl": "h-20 w-20", // 80 — marketing
      },
      tone: {
        // default primary treatment — bg-primary container, primary-foreground mark
        primary: "bg-primary text-primary-foreground",
        // steel treatment — navy gradient container for brand/office surfaces.
        // Visual values come from CSS vars so they can be overridden per-theme
        // without touching this file. Fallbacks match the canonical navy from
        // design-handoff.md §5 (#1B4F7E → #0A2540). The box-shadow and inset
        // highlight are also exposed but only applied via inline style below
        // because Tailwind cannot reference arbitrary CSS vars for gradient/shadow.
        steel: "text-white",
      },
    },
    defaultVariants: {
      size: "sm",
      tone: "primary",
    },
  },
);

/* ============================================================================
 * Component
 * ========================================================================= */

export interface AikoMarkProps
  extends Omit<React.HTMLAttributes<HTMLSpanElement>, "children">,
    VariantProps<typeof markVariants> {
  /**
   * Visual tone of the container.
   *
   * - `"primary"` (default) — `--primary` background with `--primary-foreground`
   *   mark. Standard inline Aiko avatar treatment.
   * - `"steel"` — navy gradient container that matches the BrandTile treatment.
   *   Reads `--steel-bg`, `--steel-border`, `--steel-fg` CSS vars (with hardcoded
   *   canonical navy fallbacks from design-handoff.md §5). BrandTile is a thin
   *   composer over this tone.
   */
  tone?: "primary" | "steel";
  /**
   * Toggle the breathing animation. Defaults to true. When the user prefers
   * reduced motion (or `animated={false}` is passed) the mark renders its
   * resting frame.
   */
  animated?: boolean;
  /**
   * When provided, the wrapper is announced via `role="img"` with this label.
   * Otherwise the mark is decorative (`aria-hidden="true"`).
   */
  "aria-label"?: string;
  /**
   * Exact pixel size for the container (width and height).
   *
   * Use when none of the named `size` variants (xs=24, sm=28, md=32, lg=40,
   * xl=56, 2xl=80) match the design requirement — for example, `sizeOverride={46}`
   * for the BrandTile use-case. When supplied, `size` is ignored and the
   * container renders with inline width/height styles. Border-radius scales
   * proportionally at 25% of the supplied size. The named variant steps remain
   * the default API; reach for this only when the grid step doesn't fit.
   */
  sizeOverride?: number;
}

export const AikoMark = React.forwardRef<HTMLSpanElement, AikoMarkProps>(
  ({ className, size, sizeOverride, tone = "primary", animated = true, ...props }, ref) => {
    const reduced = useReducedMotion();
    const shouldAnimate = animated && reduced !== true;
    const ariaLabel = props["aria-label"];
    const labelled = typeof ariaLabel === "string" && ariaLabel.length > 0;

    // Wall-clock sync: each <animate>'s `begin` is set to "-Xs" where X is
    // the current phase relative to the breath cycle. SMIL treats negative
    // begin as "started X seconds ago" so the animation plays from that
    // phase forward — every AikoMark on the page reads the same Date.now()
    // modulo BREATH_DURATION_MS, so they all line up regardless of when
    // they mounted. Set after mount via useEffect (not in initial render)
    // so server-rendered markup stays SSR-stable.
    const [beginOffset, setBeginOffset] = React.useState<string | undefined>(
      undefined,
    );
    React.useEffect(() => {
      if (!shouldAnimate) {
        setBeginOffset(undefined);
        return;
      }
      const phaseMs = Date.now() % BREATH_DURATION_MS;
      setBeginOffset(`-${(phaseMs / 1000).toFixed(3)}s`);
    }, [shouldAnimate]);

    // When sizeOverride is set, bypass CVA class-based sizing and use inline
    // styles. Border-radius scales at 25% of size (matching the CVA rounded-[25%]
    // intent). The base classes (flex, rounded-[25%], etc.) still come from the
    // CVA call — we just override the size dimension.
    const sizeStyle: React.CSSProperties | undefined = sizeOverride
      ? {
          width: sizeOverride,
          height: sizeOverride,
          borderRadius: Math.round(sizeOverride * 0.25),
        }
      : undefined;

    // Steel tone: navy gradient container. Values live in CSS vars so they can
    // be overridden per-theme. Fallbacks match the canonical recipe from
    // design-handoff.md §5 (#1B4F7E → #0A2540). border and box-shadow also
    // read CSS vars — the inset highlight + drop shadow are part of the steel
    // visual identity. All of this is inline because Tailwind cannot compute
    // arbitrary CSS var references for gradient/shadow utilities.
    const steelStyle: React.CSSProperties =
      tone === "steel"
        ? {
            background:
              "linear-gradient(160deg, var(--steel-bg, #1B4F7E) 0%, var(--steel-bg-to, #0A2540) 100%)",
            border: "1px solid var(--steel-border, rgba(255,255,255,0.12))",
            boxShadow: [
              "inset 0 1px 0 rgba(255,255,255,0.18)",
              "0 6px 14px -6px rgba(10,37,64,0.55)",
            ].join(", "),
            color: "var(--steel-fg, #ffffff)",
          }
        : {};

    return (
      <span
        ref={ref}
        className={cn(markVariants({ size: sizeOverride ? undefined : size, tone }), className)}
        style={{ ...sizeStyle, ...steelStyle, ...props.style }}
        role={labelled ? "img" : undefined}
        aria-hidden={labelled ? undefined : true}
        {...props}
      >
        <svg
          viewBox={VIEWBOX}
          width="80%"
          height="80%"
          fill="none"
          stroke={MARK_STROKE}
          strokeWidth={36}
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
          focusable="false"
        >
          {/* Hex outline (the "head") — five-segment bottom + sides, fixed shape */}
          <path d="M 413 167 L 413 367 L 240 467 L 67 367 L 67 167" />

          {/* Breathing top — morphs the upper crown across the breath cycle */}
          <path d="M 67 167 L 153 117 L 240 67 L 326 117 L 413 167">
            {shouldAnimate ? (
              <animate
                attributeName="d"
                dur={BREATH_DURATION}
                begin={beginOffset}
                repeatCount="indefinite"
                calcMode="spline"
                keyTimes="0; 0.5; 1"
                keySplines="0.42 0 0.4 1; 0.42 0 0.4 1"
                values="M 67 167 L 153 117 L 240 67 L 326 117 L 413 167;
                        M 67 167 L 153 67 L 240 130 L 326 67 L 413 167;
                        M 67 167 L 153 117 L 240 67 L 326 117 L 413 167"
              />
            ) : null}
          </path>

          {/* Eyeball — bounces vertically inside the head */}
          <circle cx={240} cy={240} r={22} fill={MARK_STROKE} stroke="none">
            {shouldAnimate ? (
              <animate
                attributeName="cy"
                dur={BREATH_DURATION}
                begin={beginOffset}
                repeatCount="indefinite"
                calcMode="spline"
                keyTimes="0; 0.5; 1"
                keySplines="0.42 0 0.4 1; 0.42 0 0.4 1"
                values="240; 295; 240"
              />
            ) : null}
          </circle>

          {/* Eyes — two short strokes that flash visible briefly each cycle */}
          <line
            x1={178}
            y1={155}
            x2={200}
            y2={170}
            strokeWidth={9}
            opacity={shouldAnimate ? 0 : 1}
          >
            {shouldAnimate ? (
              <animate
                attributeName="opacity"
                dur={BREATH_DURATION}
                begin={beginOffset}
                repeatCount="indefinite"
                keyTimes={BLINK_KEYTIMES}
                values={BLINK_VALUES}
              />
            ) : null}
          </line>
          <line
            x1={280}
            y1={170}
            x2={302}
            y2={155}
            strokeWidth={9}
            opacity={shouldAnimate ? 0 : 1}
          >
            {shouldAnimate ? (
              <animate
                attributeName="opacity"
                dur={BREATH_DURATION}
                begin={beginOffset}
                repeatCount="indefinite"
                keyTimes={BLINK_KEYTIMES}
                values={BLINK_VALUES}
              />
            ) : null}
          </line>
        </svg>
      </span>
    );
  },
);
AikoMark.displayName = "AikoMark";
